import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { LearnerHomeFocusSkill } from '@app/learner-home/learner-home-api.model';
import { LearnerHomeFocusSkillsService } from '@app/learner-home/services/learner-home-focus-skills.service';
import { AuthService } from '@app/shared/services/auth.service';
import { SearchUrlService } from '@app/shared/services/search-url.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TranslateService } from '@ngx-translate/core';

import { AuthUser } from '@app/account/account-api.model';
import { getTagCompletedRatings } from '@app/shared/utils/tag-helpers';
import { DgError } from '@app/shared/models/dg-error';
import { TagsApi } from '@app/tags/tag-api.model';
import { TagsSearchItem } from '@app/tags/tags';

import { QuickAddFocusSkillPopoverComponent } from './quick-add-focus-skill-popover/quick-add-focus-skill-popover.component';
import { LearnerHomeTrackerService } from '@app/learner-home/services/learner-home-tracker.service';
import { NotifierService } from '@dg/shared-services';

@Component({
  selector: 'dgx-learner-home-focus-skills',
  templateUrl: './learner-home-focus-skills.component.html',
  styleUrls: ['./learner-home-focus-skills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearnerHomeFocusSkillsComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public readonly maxFocusSkills = 4;
  @ViewChild('popoverTrigger') public popoverTriggerRef: ElementRef;
  @ViewChild('quickAddRef')
  public quickAddRef: QuickAddFocusSkillPopoverComponent;
  public authUser: AuthUser;
  public isLoading = true;
  public addFocusSkillPopoverOpen: boolean = false;
  public focusSkills: LearnerHomeFocusSkill[] = [];

  public i18n = this.translateService.instant([
    'dgProfileOverview_FocusSkills',
    'DashboardNav_AddFocusSkills',
    'DashboardNav_MaxFocusSkillDesc',
    'dgOrgSecurity_ManageSkills',
    'TagsSvc_UpdateFocusSkillsError',
    'dgProfileOverview_FocusSkills_EmptyState',
  ]);

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private learnerHomeFocusSkillsService: LearnerHomeFocusSkillsService,
    private tagRatingService: TagRatingService,
    private cdr: ChangeDetectorRef,
    private tagsService: TagsService,
    private searchUrlService: SearchUrlService,
    private learnerHomeTrackerService: LearnerHomeTrackerService,
    private notifierService: NotifierService
  ) {
    super();
  }

  get focusSkillsUrl(): string {
    let url: string;
    const profileUrl = this.authUser.viewerProfile.profileUrl;
    url = `/profile/${profileUrl}/skills`;
    return url;
  }

  public ngOnInit(): void {
    this.authUser = this.authService.authUser;
    this.learnerHomeFocusSkillsService.focusSkills$
      .pipe(this.takeUntilDestroyed())
      .subscribe((data) => {
        this.focusSkills = data.map((skill) => ({
          ...skill,
          searchUrl: this.searchUrlService.getGlobalSearchURL(skill.name),
          completedRatings: getTagCompletedRatings(skill, true),
        }));
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }

  public trackClick(itemClicked: string, data?: LearnerHomeFocusSkill) {
    this.learnerHomeTrackerService.navigationClicked({
      itemClicked,
      context: 'Sidebar',
      isFocusSkill: data?.isFocused,
      skillId: data?.tagId,
      skillName: data?.name,
    });
  }

  public togglePopover() {
    this.addFocusSkillPopoverOpen = !this.addFocusSkillPopoverOpen;
    this.cdr.detectChanges();
  }

  public skillSelected(item: TagsSearchItem) {
    if (!!this.focusSkills.find((skill) => skill.tagId === item.id)) {
      return this.notifierService.showSuccess(
        this.translateService.instant('TagsSvc_FocusSkillAlreadyAdded')
      );
    }

    this.tagsService
      .toggleUserInterestFocus(item as TagsApi.TagDetails)
      .pipe(
        finalize(() => {
          this.addFocusSkillPopoverOpen = false;
          this.cdr.detectChanges();
        }),
        catchError((error) =>
          throwError(
            new DgError(this.i18n.TagsSvc_UpdateFocusSkillsError, error)
          )
        )
      )
      .subscribe();
  }

  /**
   * Popover viewed for focus skills on the left aside on learner home
   *
   * @param skill
   */
  public onSkillRatingPopoverViewed(skill: LearnerHomeFocusSkill) {
    this.learnerHomeTrackerService.skillPopoverViewed(skill.tagId, skill.name);
  }

  /**
   * Click for focus skill on the left aside on learner home
   * @param event
   * @param skill
   */
  public openSkillRatingModal(
    event: Event,
    skill: LearnerHomeFocusSkill
  ): void {
    this.tagRatingService.openSkillModal(event, skill, false);
  }
}
